import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormState, useWatch } from 'react-hook-form';

import { useGetDiscounts } from 'api/hooks/private/finance/GetDiscounts';

import NumericFormatInput from 'components/Forms/NumericFormatInput';
import SelectInput from 'components/Forms/SelectInput';

import { ChargeFormValues } from '../ChargeSchema';

const TypeOptions = [
  {
    label: 'Fixed',
    value: 'fixed',
  },
  {
    label: 'Percentage',
    value: 'percentage',
  },
];

interface DiscountFieldProps {
  index: number;
  name: `student_charge_discounts.${number}`;
  remove: (index?: number | number[]) => void;
}

function DiscountField({ index, name, remove }: DiscountFieldProps) {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const { data: discounts } = useGetDiscounts();

  const { errors } = useFormState<ChargeFormValues>({ name: name });
  const fieldError = errors.student_charge_discounts?.[index];

  const discountField = useWatch<ChargeFormValues, `student_charge_discounts.${number}`>({ name: name });

  return (
    <Grid container rowSpacing={1} columnSpacing={1}>
      <Grid size={12} sx={{ mb: 0 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            textDecoration: 'underline',
          }}
        >
          {`Discount ${discountField?.order}`}
        </Typography>
        <Typography color="error" fontWeight="bold">
          {fieldError?.root?.message}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <SelectInput
          name={`${name}.discount_id`}
          label="Discount"
          options={discounts?.map((discount) => {
            return { label: discount.name, value: discount.id };
          })}
          sx={{
            minWidth: '100px',
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <SelectInput
          name={`${name}.type`}
          label="Type"
          options={TypeOptions}
          sx={{
            minWidth: '100px',
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <NumericFormatInput
          name={`${name}.amount`}
          label="Amount"
          multiplier={discountField?.type === 'fixed' ? 100 : 1}
          numericFormatProps={{
            allowLeadingZeros: false,
            allowNegative: false,
            decimalScale: discountField?.type === 'fixed' ? 2 : 0,
            fixedDecimalScale: true,
            thousandSeparator: ',',
            thousandsGroupStyle: 'thousand',
            isAllowed: ({ floatValue }) => {
              return discountField?.type === 'fixed' || (floatValue ?? 0) <= 100;
            },
          }}
          slotProps={{
            input: {
              startAdornment: discountField?.type === 'fixed' && <InputAdornment position="start">$</InputAdornment>,
              endAdornment: discountField?.type === 'percentage' && <InputAdornment position="end">%</InputAdornment>,
            },
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 1 }} alignSelf="flex-end">
        {isMd && (
          <IconButton color="error" aria-label="delete" onClick={() => remove(index)}>
            <DeleteIcon />
          </IconButton>
        )}

        {!isMd && (
          <Button variant="contained" size="small" color="error" fullWidth={!isMd} onClick={() => remove(index)}>
            Remove Discount {index + 1}
          </Button>
        )}
      </Grid>
    </Grid>
  );
}

export default DiscountField;
